import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';

/**
 * Loaders under ~150ms don't need a progress bar — showing and immediately
 * hiding one reads as a flicker rather than feedback.
 */
const REVEAL_DELAY_MS = 150;

/**
 * Thin animated bar pinned to the top of the viewport while React Router is
 * loading data for a route transition (e.g. a page-level `clientLoader`).
 * Without this, a slow loader makes navigation look unresponsive — the URL
 * and sidebar don't change and nothing renders until the loader resolves.
 *
 * Gated on the destination's pathname actually differing from the current
 * one, so same-route navigations (e.g. `setSearchParams` for a tab or
 * filter) never paint the bar.
 */
export function RouteLoadingBar() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const isRouteChange = navigation.state !== 'idle' && navigation.location?.pathname !== location.pathname;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(isRouteChange), isRouteChange ? REVEAL_DELAY_MS : 0);
    return () => clearTimeout(timer);
  }, [isRouteChange]);

  if (!visible) return null;

  return (
    <div
      role="status"
      data-testid="route-loading-bar"
      className="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-transparent"
    >
      <span className="sr-only">{t('common.loading')}</span>
      <div className="absolute h-full w-1/3 bg-primary animate-route-progress" />
    </div>
  );
}
